import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  DescriptionItem,
  DescriptionList,
} from '@billda/design-system';
import type { UserBusinessDetailsDto } from '../../../api/userManagementApi';

interface UserBusinessDetailsCardProps {
  businessDetails: UserBusinessDetailsDto;
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return <DescriptionItem label={label} value={value} />;
}

export function UserBusinessDetailsCard({
  businessDetails,
}: UserBusinessDetailsCardProps) {
  // If admin skipped business details (isBookingWork = false), don't render
  if (!businessDetails.isBookingWork) {
    return null;
  }

  const getBusinessType = () => {
    if (businessDetails.soleTrader) {
      return 'Sole Trader';
    }
    if (businessDetails.limitedCompany) {
      return 'Limited Company';
    }
    return 'Not specified';
  };

  return (
    <Card data-testid="user-business-details-card">
      <CardHeader>
        <h3 className="type-h6">Business Details</h3>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="flex flex-col gap-4">
          <DescriptionList className="type-body-md max-w-2xl">
            {/* Business Type */}
            <DetailRow label="Business Type" value={getBusinessType()} />

            {/* Trading Name (if present) */}
            {businessDetails.businessName && (
              <DetailRow
                label="Trading Name"
                value={businessDetails.businessName}
              />
            )}

            {/* Sole Trader Fields */}
            {businessDetails.soleTrader && (
              <>
                {businessDetails.soleTrader.hmrcFullName && (
                  <DetailRow
                    label="HMRC Full Name"
                    value={businessDetails.soleTrader.hmrcFullName}
                  />
                )}
                {businessDetails.soleTrader.utr && (
                  <DetailRow
                    label="UTR"
                    value={businessDetails.soleTrader.utr}
                  />
                )}
                {businessDetails.soleTrader.nationalInsuranceNumber && (
                  <DetailRow
                    label="National Insurance Number"
                    value={businessDetails.soleTrader.nationalInsuranceNumber}
                  />
                )}
              </>
            )}

            {/* Limited Company Fields */}
            {businessDetails.limitedCompany && (
              <>
                {businessDetails.limitedCompany.companyName && (
                  <DetailRow
                    label="Registered Company Name"
                    value={businessDetails.limitedCompany.companyName}
                  />
                )}
                {businessDetails.limitedCompany.utr && (
                  <DetailRow
                    label="Company UTR"
                    value={businessDetails.limitedCompany.utr}
                  />
                )}
                {businessDetails.limitedCompany.companyRegistrationNumber && (
                  <DetailRow
                    label="Company Registration Number"
                    value={
                      businessDetails.limitedCompany.companyRegistrationNumber
                    }
                  />
                )}
              </>
            )}

            {/* Completion Status */}
            <DescriptionItem
              label="Status"
              value={
                <Chip
                  label={businessDetails.isComplete ? 'Complete' : 'Incomplete'}
                  size="sm"
                  color={businessDetails.isComplete ? 'success' : 'warning'}
                />
              }
            />
          </DescriptionList>
        </div>
      </CardContent>
    </Card>
  );
}
